import { useState } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import {
  ArrowLeft20Regular,
  ArrowUpload20Regular,
  CheckmarkCircle20Filled,
  Document20Regular,
  Warning20Regular,
  PeopleTeam20Regular,
  DocumentText20Regular,
  TableSimple20Regular,
  ChevronDoubleRight20Regular,
} from "@fluentui/react-icons";
import { useNavigate } from "react-router";
import {
  importService,
  ImportKind,
  PreviewSummary,
  CommitResult,
} from "../services/importService";
import { ImportReviewStep } from "../components/import/ImportReviewStep";

type Step = "upload" | "review" | "commit";

const useStyles = makeStyles({
  root: {
    paddingTop: "24px",
    paddingBottom: "48px",
    paddingLeft: "32px",
    paddingRight: "32px",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    maxWidth: "1400px",
    marginLeft: "auto",
    marginRight: "auto",
    display: "flex",
    flexDirection: "column",
    rowGap: "20px",
  },
  backBtn: {
    alignSelf: "flex-start",
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ...shorthands.padding("6px", "12px"),
    backgroundColor: "transparent",
    ...shorthands.border("1px", "solid", "transparent"),
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    cursor: "pointer",
    ...shorthands.borderRadius("6px"),
    ":hover": { backgroundColor: BRAND.bg },
  },

  // ---- Header bar (Risikoforms style) ----
  bar: {
    backgroundColor: BRAND.purple,
    backgroundImage: `linear-gradient(135deg, ${BRAND.purple} 0%, ${BRAND.ink} 100%)`,
    color: BRAND.white,
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "24px",
    paddingRight: "24px",
    ...shorthands.borderRadius("10px"),
    fontFamily: BRAND.fontPrimary,
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    boxShadow: "0 12px 24px rgba(45,20,43,0.18)",
  },
  goldLine: {
    height: "3px",
    backgroundColor: BRAND.gold,
    ...shorthands.borderRadius("999px"),
    width: "60px",
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "12px",
  },
  titleIcon: {
    width: "40px",
    height: "40px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(255,255,255,0.1)",
    color: BRAND.gold,
    ...shorthands.borderRadius("999px"),
  },
  title: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.4rem, 3.4vw, 2rem)",
    fontWeight: 400,
    color: BRAND.gold,
    flexGrow: 1,
  },
  subtitle: { color: "rgba(255,255,255,0.85)", fontSize: "0.9rem" },
  steps: {
    display: "flex",
    columnGap: "10px",
    fontSize: "0.85rem",
    color: "rgba(255,255,255,0.7)",
    marginTop: "4px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  stepActive: {
    color: BRAND.gold,
    fontWeight: 700,
  },

  // ---- Body card ----
  body: {
    backgroundColor: BRAND.white,
    paddingTop: "24px",
    paddingBottom: "24px",
    paddingLeft: "24px",
    paddingRight: "24px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
  },

  // ---- Kind picker ----
  kindRow: {
    display: "flex",
    columnGap: "16px",
    marginBottom: "24px",
    flexWrap: "wrap",
  },
  kindBtn: {
    flexGrow: 1,
    flexBasis: "320px",
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "20px",
    paddingRight: "20px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    textAlign: "left",
    display: "flex",
    columnGap: "14px",
    alignItems: "flex-start",
    transitionProperty: "background-color, border-color",
    transitionDuration: "120ms",
    ":hover": {
      backgroundColor: BRAND.bg,
      ...shorthands.borderColor(BRAND.purple),
    },
  },
  kindBtnActive: {
    ...shorthands.borderColor(BRAND.purple),
    backgroundColor: "#f5eef6",
    boxShadow: `0 0 0 3px ${BRAND.purple}22`,
  },
  kindIcon: {
    width: "44px",
    height: "44px",
    minWidth: "44px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BRAND.bg,
    color: BRAND.purple,
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
  },
  kindBody: {
    display: "flex",
    flexDirection: "column",
    rowGap: "4px",
  },
  kindLabel: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.15rem",
    color: BRAND.ink,
  },
  kindHint: { color: BRAND.muted, fontSize: "0.85rem", lineHeight: 1.4 },

  // ---- Drop zone ----
  dropZone: {
    ...shorthands.border("2px", "dashed", BRAND.borderDashed),
    ...shorthands.borderRadius("12px"),
    paddingTop: "48px",
    paddingBottom: "48px",
    paddingLeft: "16px",
    paddingRight: "16px",
    textAlign: "center",
    color: BRAND.muted,
    cursor: "pointer",
    backgroundColor: BRAND.bg,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    rowGap: "10px",
    transitionProperty: "background-color, border-color",
    transitionDuration: "120ms",
    ":hover": { backgroundColor: "#f5eef6", ...shorthands.borderColor(BRAND.purple) },
  },
  dropZoneActive: {
    backgroundColor: "#f5eef6",
    ...shorthands.borderColor(BRAND.purple),
  },
  uploadIconCircle: {
    width: "56px",
    height: "56px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BRAND.white,
    color: BRAND.purple,
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
  },
  fileInfo: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    color: BRAND.ink,
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
  },

  // ---- Buttons ----
  primaryBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.purple,
    backgroundImage: "none",
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    fontWeight: 600,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "20px",
    paddingRight: "20px",
    ...shorthands.border("1px", "solid", BRAND.purple),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    ":hover": { backgroundColor: BRAND.ink },
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
  secondaryBtn: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    paddingTop: "9px",
    paddingBottom: "9px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("8px"),
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    ":hover": { backgroundColor: BRAND.bg },
  },

  // ---- Summary cards ----
  summaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "12px",
    marginBottom: "20px",
  },
  summaryCard: {
    paddingTop: "16px",
    paddingBottom: "16px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    backgroundColor: BRAND.bg,
    display: "flex",
    columnGap: "12px",
    alignItems: "center",
  },
  summaryIcon: {
    width: "36px",
    height: "36px",
    minWidth: "36px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.borderRadius("999px"),
    color: BRAND.white,
  },
  summaryBody: { display: "flex", flexDirection: "column", rowGap: "2px" },
  summaryLabel: {
    fontSize: "0.7rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
  },
  summaryValue: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.5rem",
    color: BRAND.ink,
    lineHeight: 1.1,
  },

  // ---- Boxes & spinner ----
  errorBox: {
    backgroundColor: "#fdecec",
    color: "#8b1c1c",
    paddingTop: "12px",
    paddingBottom: "12px",
    paddingLeft: "16px",
    paddingRight: "16px",
    ...shorthands.border("1px", "solid", "#f5b3b3"),
    ...shorthands.borderRadius("8px"),
    marginTop: "12px",
    display: "flex",
    columnGap: "8px",
    alignItems: "center",
  },
  successHeader: {
    margin: 0,
    fontFamily: BRAND.fontAccent,
    fontSize: "1.4rem",
    color: BRAND.ink,
    display: "inline-flex",
    alignItems: "center",
    columnGap: "10px",
    marginBottom: "16px",
  },
  spinnerWrap: {
    display: "inline-flex",
    columnGap: "4px",
    alignItems: "center",
  },
  spinnerDot: {
    display: "inline-block",
    width: "8px",
    height: "8px",
    backgroundColor: BRAND.gold,
    ...shorthands.borderRadius("999px"),
    animationName: {
      "0%, 80%, 100%": { opacity: 0.2 },
      "40%": { opacity: 1 },
    },
    animationDuration: "1.4s",
    animationIterationCount: "infinite",
  },
  spinnerDot2: { animationDelay: "0.16s" },
  spinnerDot3: { animationDelay: "0.32s" },
});

const Spinner3: React.FC = () => {
  const styles = useStyles();
  return (
    <span className={styles.spinnerWrap} aria-label="Lade…">
      <span className={styles.spinnerDot} />
      <span className={mergeClasses(styles.spinnerDot, styles.spinnerDot2)} />
      <span className={mergeClasses(styles.spinnerDot, styles.spinnerDot3)} />
    </span>
  );
};

const SUMMARY_COLORS = {
  total: BRAND.purple,
  ok: "#0e7c3a",
  hard: "#b91c1c",
  soft: "#a06b16",
  invalid: "#5d5664",
};

const ImportPage = () => {
  const styles = useStyles();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("upload");
  const [kind, setKind] = useState<ImportKind>("contacts");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState<PreviewSummary | null>(null);
  const [commitResult, setCommitResult] = useState<CommitResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const onFile = (f: File | null) => {
    setFile(f);
    setError(null);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const res = await importService.preview(kind, file);
      setSummary(res);
      setStep("review");
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setUploading(false);
    }
  };

  const handleCommit = async () => {
    if (!summary) return;
    setUploading(true);
    setError(null);
    try {
      const res = await importService.commit(summary.batchId);
      setCommitResult(res);
      setStep("commit");
    } catch (e: any) {
      setError(String(e?.message ?? e));
    } finally {
      setUploading(false);
    }
  };

  const reset = () => {
    setStep("upload");
    setFile(null);
    setSummary(null);
    setCommitResult(null);
    setError(null);
  };

  const StepLabel: React.FC<{ active: boolean; children: React.ReactNode }> = ({
    active,
    children,
  }) => <span className={active ? styles.stepActive : ""}>{children}</span>;

  return (
    <div className={styles.root}>
      <button className={styles.backBtn} onClick={() => navigate(-1)}>
        <ArrowLeft20Regular /> Zurück
      </button>

      <div className={styles.bar}>
        <div className={styles.titleRow}>
          <span className={styles.titleIcon}>
            <TableSimple20Regular style={{ fontSize: 22 }} />
          </span>
          <h1 className={styles.title}>Excel-Import</h1>
        </div>
        <div className={styles.goldLine} />
        <span className={styles.subtitle}>
          Kundenstammdaten und Verträge aus Excel-Dateien importieren mit
          Duplikatenprüfung und Konfliktauflösung pro Zeile.
        </span>
        <div className={styles.steps}>
          <StepLabel active={step === "upload"}>1 · Hochladen</StepLabel>
          <ChevronDoubleRight20Regular style={{ fontSize: 14, opacity: 0.6 }} />
          <StepLabel active={step === "review"}>2 · Prüfen & Entscheiden</StepLabel>
          <ChevronDoubleRight20Regular style={{ fontSize: 14, opacity: 0.6 }} />
          <StepLabel active={step === "commit"}>3 · Übernehmen</StepLabel>
        </div>
      </div>

      {step === "upload" && (
        <div className={styles.body}>
          <div className={styles.kindRow}>
            <button
              type="button"
              className={mergeClasses(styles.kindBtn, (kind === "contacts") && styles.kindBtnActive)}
              onClick={() => setKind("contacts")}
            >
              <span className={styles.kindIcon}>
                <PeopleTeam20Regular style={{ fontSize: 22 }} />
              </span>
              <span className={styles.kindBody}>
                <span className={styles.kindLabel}>Kontakte</span>
                <span className={styles.kindHint}>
                  z. B. „0 Kontakt Kunden Firma … .xlsx" — Kunden, Firmen,
                  Ansprechpartner, Adressen, Bankverbindung.
                </span>
              </span>
            </button>
            <button
              type="button"
              className={mergeClasses(styles.kindBtn, (kind === "contracts") && styles.kindBtnActive)}
              onClick={() => setKind("contracts")}
            >
              <span className={styles.kindIcon}>
                <DocumentText20Regular style={{ fontSize: 22 }} />
              </span>
              <span className={styles.kindBody}>
                <span className={styles.kindLabel}>Verträge</span>
                <span className={styles.kindHint}>
                  z. B. „1a Verträge Firma … .xls" — Verträge zu zuvor
                  importierten Kunden (verbunden über Kundennummer).
                </span>
              </span>
            </button>
          </div>

          <label
            className={mergeClasses(styles.dropZone, dragOver && styles.dropZoneActive)}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer.files?.[0] ?? null;
              if (f) onFile(f);
            }}
          >
            <input
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={(e) => onFile(e.target.files?.[0] ?? null)}
            />
            <span className={styles.uploadIconCircle}>
              <ArrowUpload20Regular style={{ fontSize: 24 }} />
            </span>
            <div>
              {file ? (
                <span className={styles.fileInfo}>
                  <Document20Regular />
                  {file.name} ({(file.size / 1024 / 1024).toFixed(1)} MB)
                </span>
              ) : (
                <span>
                  Excel-Datei (.xlsx oder .xls) hier ablegen oder anklicken zum
                  Auswählen
                </span>
              )}
            </div>
          </label>

          {error && (
            <div className={styles.errorBox}>
              <Warning20Regular /> {error}
            </div>
          )}

          <div
            style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}
          >
            <button
              type="button"
              className={styles.primaryBtn}
              disabled={!file || uploading}
              onClick={handleUpload}
            >
              {uploading ? <Spinner3 /> : <ArrowUpload20Regular />}
              {uploading ? "Wird geprüft…" : "Datei prüfen"}
            </button>
          </div>
        </div>
      )}

      {step === "review" && summary && (
        <>
          <div className={styles.body}>
            <div className={styles.summaryGrid}>
              <SummaryCard
                icon={<TableSimple20Regular />}
                color={SUMMARY_COLORS.total}
                label="Zeilen gesamt"
                value={summary.totalRows}
              />
              <SummaryCard
                icon={<CheckmarkCircle20Filled />}
                color={SUMMARY_COLORS.ok}
                label="Ohne Konflikt"
                value={summary.noConflict}
              />
              <SummaryCard
                icon={<Warning20Regular />}
                color={SUMMARY_COLORS.hard}
                label="Harte Konflikte"
                value={summary.hardConflicts}
              />
              <SummaryCard
                icon={<Warning20Regular />}
                color={SUMMARY_COLORS.soft}
                label="Weiche Konflikte"
                value={summary.softConflicts}
              />
              <SummaryCard
                icon={<Warning20Regular />}
                color={SUMMARY_COLORS.invalid}
                label="Ungültig"
                value={summary.invalid}
              />
            </div>
            <ImportReviewStep batchId={summary.batchId} kind={kind} />
          </div>

          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button type="button" className={styles.secondaryBtn} onClick={reset}>
              <ArrowLeft20Regular /> Datei wechseln
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              disabled={uploading}
              onClick={handleCommit}
            >
              {uploading ? <Spinner3 /> : <CheckmarkCircle20Filled />}
              {uploading ? "Import läuft…" : "Import durchführen"}
            </button>
          </div>

          {error && (
            <div className={styles.errorBox}>
              <Warning20Regular /> {error}
            </div>
          )}
        </>
      )}

      {step === "commit" && commitResult && (
        <div className={styles.body}>
          <h2 className={styles.successHeader}>
            <CheckmarkCircle20Filled style={{ color: "#0e7c3a", fontSize: 28 }} />
            Import abgeschlossen
          </h2>
          <div className={styles.summaryGrid}>
            <SummaryCard
              icon={<CheckmarkCircle20Filled />}
              color={SUMMARY_COLORS.ok}
              label="Erstellt"
              value={commitResult.created}
            />
            <SummaryCard
              icon={<CheckmarkCircle20Filled />}
              color={BRAND.purple}
              label="Aktualisiert"
              value={commitResult.updated}
            />
            <SummaryCard
              icon={<CheckmarkCircle20Filled />}
              color={BRAND.gold}
              label="Zusammengeführt"
              value={commitResult.merged}
            />
            <SummaryCard
              icon={<Document20Regular />}
              color={SUMMARY_COLORS.invalid}
              label="Übersprungen"
              value={commitResult.skipped}
            />
            <SummaryCard
              icon={<Warning20Regular />}
              color={SUMMARY_COLORS.hard}
              label="Fehlgeschlagen"
              value={commitResult.failed}
            />
          </div>
          {commitResult.failed > 0 && (
            <div className={styles.errorBox}>
              <Warning20Regular />
              Einige Zeilen konnten nicht importiert werden. Details siehe
              ImportBatch in der Datenbank.
            </div>
          )}
          <div style={{ marginTop: 20 }}>
            <button type="button" className={styles.secondaryBtn} onClick={reset}>
              <ArrowUpload20Regular /> Weiteren Import starten
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SummaryCard: React.FC<{
  icon: React.ReactNode;
  color: string;
  label: string;
  value: number;
}> = ({ icon, color, label, value }) => {
  const styles = useStyles();
  return (
    <div className={styles.summaryCard}>
      <span className={styles.summaryIcon} style={{ backgroundColor: color }}>
        {icon}
      </span>
      <span className={styles.summaryBody}>
        <span className={styles.summaryLabel}>{label}</span>
        <span className={styles.summaryValue}>
          {value.toLocaleString("de-DE")}
        </span>
      </span>
    </div>
  );
};

export default ImportPage;
